/**
 * MÉTRICAS REAIS DA FONTE ROBOTO (embutida no pdfmake) — tabela de largura
 * por caractere, calibrada a partir da fonte TTF real (extraída de
 * `pdfmake/build/vfs_fonts.js`) usando `pdfkit` (dependência do próprio
 * pdfmake) em `fontSize(1)`, ou seja, largura por 1pt de corpo — escala
 * linearmente com o tamanho da fonte.
 *
 * Isso existe para o algoritmo de autoajuste da Lista de Linhas (comando
 * "autoajuste inteligente") calcular a largura mínima real de qualquer
 * coluna/valor SEM depender de `pdfkit` em runtime (que usa `fs` do Node e
 * não roda no navegador, onde o app real gera os PDFs — ver CLAUDE.md,
 * `BrowserPlatform`). A tabela é dado medido, não estimativa arbitrária.
 *
 * Caractere fora da tabela usa a largura média medida (`AVG_*`) como
 * fallback seguro — nunca undefined/NaN.
 */

const ROBOTO_REGULAR_WIDTHS: Readonly<Record<string, number>> = {
  "0": 0.56201, "1": 0.56201, "2": 0.56201, "3": 0.56201, "4": 0.56201, "5": 0.56201, "6": 0.56201, "7": 0.56201, "8": 0.56201, "9": 0.56201,
  "A": 0.65234, "B": 0.62305, "C": 0.65088, "D": 0.65625, "E": 0.56836, "F": 0.55273, "G": 0.68115, "H": 0.71338, "I": 0.27197, "J": 0.55176,
  "K": 0.62744, "L": 0.53857, "M": 0.87305, "N": 0.71338, "O": 0.68799, "P": 0.63086, "Q": 0.68799, "R": 0.61621, "S": 0.59375, "T": 0.59668,
  "U": 0.64844, "V": 0.63672, "W": 0.88721, "X": 0.62695, "Y": 0.60059, "Z": 0.59912,
  "a": 0.54395, "b": 0.56152, "c": 0.52344, "d": 0.56396, "e": 0.53027, "f": 0.34766, "g": 0.56152, "h": 0.55078, "i": 0.24316, "j": 0.23926,
  "k": 0.50684, "l": 0.24316, "m": 0.87695, "n": 0.55225, "o": 0.57031, "p": 0.56152, "q": 0.56836, "r": 0.33887, "s": 0.51611, "t": 0.32715,
  "u": 0.55127, "v": 0.48438, "w": 0.75146, "x": 0.49609, "y": 0.47314, "z": 0.49609,
  "Á": 0.65234, "Â": 0.65234, "Ã": 0.65234, "À": 0.65234, "É": 0.56836, "Ê": 0.56836, "Í": 0.27197, "Ó": 0.68799, "Ô": 0.68799, "Õ": 0.68799, "Ú": 0.64844, "Ç": 0.65088,
  "á": 0.54395, "â": 0.54395, "ã": 0.54395, "à": 0.54395, "é": 0.53027, "ê": 0.53027, "í": 0.24756, "ó": 0.57031, "ô": 0.57031, "õ": 0.57031, "ú": 0.55127, "ç": 0.52344,
  "\"": 0.32031, "'": 0.1748, ".": 0.26367, ",": 0.19678, ":": 0.24219, ";": 0.21143, "-": 0.27637, "/": 0.4126, "#": 0.61572, "&": 0.62207, "%": 0.73242,
  "°": 0.37354, "²": 0.3667, "³": 0.3667, "(": 0.34229, ")": 0.34814, "[": 0.26514, "]": 0.26514, "+": 0.56738, "*": 0.43066, "_": 0.45117, "@": 0.89795, "=": 0.54883,
  "º": 0.45508, "ª": 0.44727, " ": 0.24805
};

const ROBOTO_MEDIUM_WIDTHS: Readonly<Record<string, number>> = {
  "0": 0.56836, "1": 0.56836, "2": 0.56836, "3": 0.56836, "4": 0.56836, "5": 0.56836, "6": 0.56836, "7": 0.56836, "8": 0.56836, "9": 0.56836,
  "A": 0.66309, "B": 0.63135, "C": 0.65283, "D": 0.65283, "E": 0.56494, "F": 0.55078, "G": 0.68115, "H": 0.70947, "I": 0.2832, "J": 0.55566,
  "K": 0.63184, "L": 0.54004, "M": 0.87451, "N": 0.70947, "O": 0.68896, "P": 0.63818, "Q": 0.68896, "R": 0.62939, "S": 0.60547, "T": 0.60938,
  "U": 0.6543, "V": 0.64551, "W": 0.88037, "X": 0.63135, "Y": 0.61084, "Z": 0.60303,
  "a": 0.53906, "b": 0.56201, "c": 0.52197, "d": 0.56348, "e": 0.53564, "f": 0.35303, "g": 0.56689, "h": 0.55566, "i": 0.25488, "j": 0.25098,
  "k": 0.52148, "l": 0.25488, "m": 0.87109, "n": 0.55664, "o": 0.56738, "p": 0.56201, "q": 0.56592, "r": 0.35352, "s": 0.51465, "t": 0.33301,
  "u": 0.55615, "v": 0.49609, "w": 0.74219, "x": 0.50293, "y": 0.48975, "z": 0.50293,
  "Á": 0.66309, "Â": 0.66309, "Ã": 0.66309, "À": 0.66309, "É": 0.56494, "Ê": 0.56494, "Í": 0.2832, "Ó": 0.68896, "Ô": 0.68896, "Õ": 0.68896, "Ú": 0.6543, "Ç": 0.65283,
  "á": 0.53906, "â": 0.53906, "ã": 0.53906, "à": 0.53906, "é": 0.53564, "ê": 0.53564, "í": 0.26123, "ó": 0.56738, "ô": 0.56738, "õ": 0.56738, "ú": 0.55615, "ç": 0.52197,
  "\"": 0.31934, "'": 0.16748, ".": 0.27783, ",": 0.22363, ":": 0.26367, ";": 0.23975, "-": 0.33984, "/": 0.39014, "#": 0.60303, "&": 0.64111, "%": 0.73633,
  "°": 0.38232, "²": 0.36963, "³": 0.36963, "(": 0.34619, ")": 0.34961, "[": 0.27148, "]": 0.27148, "+": 0.55518, "*": 0.44336, "_": 0.44775, "@": 0.89697, "=": 0.5625,
  "º": 0.45654, "ª": 0.44531, " ": 0.24854
};

const AVG_REGULAR = 0.52839;
const AVG_MEDIUM = 0.53296;

/** Largura real (pt) de uma string na Roboto, no tamanho de fonte dado. */
export function measureTextWidth(text: string, options: { bold?: boolean; fontSize: number } ): number {
  const table = options.bold ? ROBOTO_MEDIUM_WIDTHS : ROBOTO_REGULAR_WIDTHS;
  const fallback = options.bold ? AVG_MEDIUM : AVG_REGULAR;
  let widthAt1pt = 0;
  for (const ch of text) widthAt1pt += table[ch] ?? fallback;
  return widthAt1pt * options.fontSize;
}

/**
 * Tokens "indivisíveis" para fins de largura mínima de coluna: o pdfmake
 * (via `linebreak`, algoritmo Unicode UAX#14) só quebra linha em espaço ou
 * após hífen — nunca no meio de uma palavra sem esses delimitadores. Dividir
 * por espaço E hífen aqui reflete exatamente onde o pdfmake vai realmente
 * poder quebrar, evitando superdimensionar a largura mínima por causa de um
 * código longo com muitos hifens (ex.: "P&ID-U35-SISTEMA-...-001").
 */
export function splitIntoBreakableTokens(text: string): string[] {
  return text.split(/(?<=[\s-])/).map(part => part.trimEnd()).filter(part => part.length > 0);
}

/** Maior token indivisível de uma string, pela largura real medida. */
export function measureLongestTokenWidth(text: string, options: { bold?: boolean; fontSize: number }): number {
  const tokens = splitIntoBreakableTokens(text);
  if (tokens.length === 0) return 0;
  return Math.max(...tokens.map(token => measureTextWidth(token, options)));
}
